import ADIProductSaleInfo from "@NA/Components/ProductList/ADIProductSaleInfo";
import toggleLoginDrawer from "@NA/Handlers/ADILoginDrawerHandler";
import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListActions from "@NA/Overrides/Widgets/ProductList/ProductListActions";
import ProductListProductImage from "@NA/Overrides/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import React, { FC, useEffect, useState } from "react";
import { ResolveThunks, connect, useSelector } from "react-redux";
import { css } from "styled-components";
import { GridOffset, GridWrapper } from "@insite/mobius/GridContainer/GridContainer";
import { getMaxProductHeight } from "@NA/Utils/Utility";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";

type Props = ProductCardSelections &
    HasProduct &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    isAuthenticated: state.context.session.isAuthenticated,
});

const mapDispatchToProps = {
    toggleLoginDrawer,
};

export interface ProductListProductGridCardStyles {
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    signInDealerPrice?: InjectableCss;
    sponsoredText?: TypographyPresentationProps;
    dealerPriceText?: TypographyProps;
    tags?: TypographyProps;
    tagsContainer?: InjectableCss;
    variantContainer?: InjectableCss;
    isPendingVerificationSignIn?: InjectableCss;
}

export const productGridCardStyles: ProductListProductGridCardStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            border: 1px solid ${getColor("text.disabled")};
            border-radius: 5px;
            position: relative;
            justify-content: space-between;
        `,
    },
    isPendingVerificationSignIn: {
        css: css`
            flex-grow: 0;
            padding-top: 35px;
            margin: 20px;
            cursor: auto;
            > div:hover {
                background-color: transparent;
                span {
                    color: ${ADIColors.darkBlue1};
                }
            }
            span {
                color: ${ADIColors.darkBlue1};
            }
        `,
    },
    imageWrapper: {
        css: css`
            margin: 0;
            padding: 10px;
            img {
                max-height: 160px;
                max-width: 231px;
                height: 160px;
                width: 160px;
                margin: 0 auto;
            }
        `,
    },
    sponsoredText: {
        css: css`
            font-size: 0.9em;
            opacity: 0.5;
            position: absolute;
            top: 12px;
        `,
    },
    contentWrapper: {
        css: css`
            padding: 10px 20px 0;
            position: relative;
            flex-grow: 1;
            > ${GridWrapper} {
                > ${GridOffset} {
                    width: 100%;
                    margin: 0;
                }
            }
        `,
    },
    signInDealerPrice: {
        css: css`
            border-top: 1px solid ${getColor("text.disabled")};
            text-align: center;
            padding: 20px 0;
            cursor: pointer;
            transition: 0.2s ease;
            border-bottom-right-radius: 5px;
            border-bottom-left-radius: 5px;
            // margin-top: 10px;
            :hover {
                background-color: ${getColor("primary")};
                span {
                    color: ${ADIColors.white};
                }
            }
        `,
    },
    dealerPriceText: {
        css: css`
            color: ${getColor("primary")};
            font-size: ${getProp("theme.typography.h6.size")};
            font-weight: bold;
        `,
    },
    tags: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
            padding: 4px 15px !important;
            background-color: ${ADIColors.newPrimary};
            flex-grow: 0;
            font-size: 12px;
            font-weight: 800;
            font-stretch: normal;
            font-style: normal;
            line-height: normal;
            letter-spacing: normal;
            text-align: center;
            color: ${ADIColors.white};
            border-radius: 0;
            border-top-left-radius: 5px;
            border-bottom-right-radius: 5px;
            min-height: 24px;
            width: auto;
            line-height: 1;
            white-space: normal;
            height: auto;
        `,
    },
    tagsContainer: {
        css: css`
            position: absolute;
            top: 0;
            left: 0;
        `,
    },
    variantContainer: {
        css: css`
            padding: 5px 20px 10px;
            font-size: 12px;
            font-weight: bold;
            color: #002856;
        `,
    },
};

const styles = productGridCardStyles;

const ProductListProductGridCard: FC<Props> = ({
    product,
    productInfo,
    showImage,
    showCompare,
    showAttributes,
    showAvailability,
    showBrand,
    showPartNumbers,
    showTitle,
    showAddToList,
    showPrice,
    isAuthenticated,
    toggleLoginDrawer,
}) => {
    const openSignIn = () => {
        toggleLoginDrawer({
            loginDrawer: true,
        });
    };

    const pricing = productInfo.pricing || product.pricing;

    const showAuthorizationRequired = product?.properties?.showAuthorizationRequired;
    
    const isPendingVerification = useSelector(
        (state: CustomApplicationState) => state.ADIGlobalData.PricingStatusStore.isPendingVerification,
    );
    const [minVariantHeight, setMinVariantHeight] = useState(15);

    useEffect(() => {
        const maxVariantHeight = getMaxProductHeight('[data-test-selector="moreOptionsAvailable"]');
        setMinVariantHeight(maxVariantHeight);
    }, []);

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector={`productListProductCard${product.id}`}>
            {isAuthenticated ? (
                <ADIProductSaleInfo product={product} isAuthenticated={true} pricing={pricing} />
            ) : (
                <ADIProductSaleInfo product={product} isAuthenticated={false} pricing={pricing} />
            )}
            <StyledWrapper {...styles.imageWrapper}>
                {product.isSponsored && <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>}
                <ProductListProductImage showImage={showImage} showCompare={showCompare} />
            </StyledWrapper>
            <StyledWrapper {...styles.contentWrapper}>
                <ProductListProductInformation
                    showAttributes={showAttributes}
                    showAvailability={showAvailability}
                    showBrand={showBrand}
                    showPartNumbers={showPartNumbers}
                    showTitle={showTitle}
                />
            </StyledWrapper>
            {!isAuthenticated ? (
                <StyledWrapper>
                    <StyledWrapper
                        {...styles.variantContainer}
                        data-test-selector="moreOptionsAvailable"
                        style={{ minHeight: minVariantHeight + "px" }}
                    >
                        {product.variantTypeId && <Typography>More Options Available</Typography>}
                    </StyledWrapper>
                    <StyledWrapper {...styles.signInDealerPrice} onClick={openSignIn}>
                        <Typography {...styles.dealerPriceText} as={"span"}>
                            {translate("Sign In for Dealer Pricing")}
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
            ) : null}
            {isAuthenticated ? <ProductListActions showAddToList={showAddToList} showPrice={showPrice} /> : null}
            {isAuthenticated && isPendingVerification && (
                <StyledWrapper {...styles.isPendingVerificationSignIn}>
                    <Typography {...styles.dealerPriceText} as="span">
                        {translate("Account Pending Verification")}
                    </Typography>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProduct(ProductListProductGridCard));
